// SPDX-License-Identifier: GPL-3.0-or-later
// The render adapter: engine frames in, pixels out. It reads RenderFrame and EngineEvent only.
// It never changes physics state.
//
// Choices:
// - WebGL2 only (three r163 and later). The renderer has an antialiased canvas.
// - A fixed camera that shows the whole playfield and every character in portrait and in
//   landscape (camera.ts). A tall character that stands out of the playfield box counts.
// - The device pixel ratio never goes above MAX_PIXEL_RATIO (coords.ts).
// - Light shadows: one directional light, one 1024 px soft shadow map, a clear plane on the
//   playfield that shows the shadows. THREE.PCFSoftShadowMap is deprecated; PCFShadowMap is soft.
// - Reduced motion (THEME.md section 11.3): no blinking (a blinking light glows steady), no
//   fade or sequence on a lamp, no flash faster than three times in one second at any setting,
//   and no camera move (there is none).
//   The page reads `prefers-reduced-motion` at start, and `setReducedMotion` is the toggle.
//   Characters read the same flag from the page (see makeCharacterEnv in src/table).
// - The backdrop is opaque black by default. `setBackdropTransparent(true)` clears to alpha 0 and
//   removes the scene background, so that a page layer behind the canvas shows through.
// - A synthwave backdrop (backdrop.ts) draws behind the table while the backdrop is opaque. It is
//   hidden when `setBackdropTransparent(true)`. It is always still.
// - The effects layer (src/render/vfx) draws into the same scene. It is created by the page.
// - The renderer calls `animate(obj, "idle", timeMs)` of each decoration at every frame.
// - On dispose the renderer calls `dispose(obj)` of each decoration factory that has one.

import {
  type Camera,
  Color,
  type Mesh,
  type Object3D,
  PCFShadowMap,
  type PerspectiveCamera,
  PMREMGenerator,
  type Scene,
  type ShadowMapType,
  type Texture,
  WebGLRenderer,
} from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import type {
  BallFrame,
  CreateRenderer,
  DecorationFactory,
  EngineEvent,
  RenderFrame,
  TableRenderer,
  TableSpec,
} from "../shared/contracts.ts";
import { type Backdrop, createBackdrop } from "./backdrop.ts";
import { createCamera, fitCamera } from "./camera.ts";
import type { CreateCanvas } from "./canvas.ts";
import { capPixelRatio } from "./coords.ts";
import { BRAND } from "./materials.ts";
import { applyBallFrame, buildScene, type SceneModel } from "./scene.ts";

/** The part of WebGLRenderer that this adapter uses. A test passes a fake. */
export interface WebGLLike {
  readonly shadowMap: { enabled: boolean; type: ShadowMapType };
  setPixelRatio(ratio: number): void;
  setSize(width: number, height: number, updateStyle?: boolean): void;
  render(scene: Scene, camera: Camera): void;
  dispose(): void;
  /** Set the clear colour and its alpha. A platform without it keeps its default (opaque black). */
  setClearColor?(color: number, alpha?: number): void;
}

/** The part of a media query list that this adapter uses. */
export interface MediaQueryLike {
  readonly matches: boolean;
  addEventListener?(type: "change", listener: () => void): void;
  removeEventListener?(type: "change", listener: () => void): void;
}

/** An environment map and the way to free it. */
export interface EnvironmentHandle {
  readonly texture: Texture;
  dispose(): void;
}

/** Everything that the adapter takes from the platform. */
export interface RendererDeps {
  createWebGL(canvas: HTMLCanvasElement): WebGLLike;
  /** An environment map for reflections. Return null for none. */
  environment?(gl: WebGLLike): EnvironmentHandle | null;
  createCanvas?: CreateCanvas;
  matchMedia?(query: string): MediaQueryLike | null;
}

/** The renderer that the client uses. It adds the reduced motion toggle to the contract. */
export interface PinballRenderer extends TableRenderer {
  /** Turn reduced motion on or off. */
  setReducedMotion(on: boolean): void;
  readonly reducedMotion: boolean;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly model: SceneModel;
  /**
   * Show the page behind the canvas (a stream layer) in place of the black backdrop. On: the
   * clear is transparent and the scene has no background. The playfield and the cabinet stay
   * opaque. Off: the opaque black backdrop (the default).
   */
  setBackdropTransparent(on: boolean): void;
  readonly backdropTransparent: boolean;
  /**
   * The synthwave backdrop behind the table (backdrop.ts). It shows while the backdrop is not
   * transparent. It is always still.
   */
  readonly backdrop: Backdrop;
  /** Height of the drawing buffer in pixels (the size of the last resize times the pixel ratio). 0 before a resize. */
  readonly pixelHeight: number;
}

/** Pseudo decoration names that carry text for the client shell. The renderer ignores them. */
const TEXT_CHANNELS: ReadonlySet<string> = new Set(["Callout", "Hud"]);

/** Media query for the reduced motion setting. */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** The platform dependencies of the browser. */
export function browserDeps(): RendererDeps {
  return {
    createWebGL: (canvas) => {
      const gl = new WebGLRenderer({
        canvas,
        antialias: true,
        // The canvas has an alpha channel, so that the backdrop can turn transparent at run time
        // (`setBackdropTransparent`). A context attribute cannot change later. The clear is opaque
        // by default, so the picture is the same as with no alpha channel.
        alpha: true,
        powerPreference: "high-performance",
      });
      return gl;
    },
    environment: (gl) => {
      const room = new RoomEnvironment();
      const pmrem = new PMREMGenerator(gl as unknown as WebGLRenderer);
      const target = pmrem.fromScene(room, 0.04);
      room.dispose();
      pmrem.dispose();
      return { texture: target.texture, dispose: () => target.dispose() };
    },
    matchMedia: (query) =>
      typeof globalThis.matchMedia === "function" ? globalThis.matchMedia(query) : null,
  };
}

/** The reduced motion flag. It follows the media query until `set` is called. */
interface ReducedMotionFlag {
  readonly on: boolean;
  set(on: boolean): void;
  /** Stop to follow the media query. */
  stop(): void;
}

function trackReducedMotion(media: MediaQueryLike | null): ReducedMotionFlag {
  let reduced = media?.matches === true;
  const onChange = (): void => {
    reduced = media?.matches === true;
  };
  media?.addEventListener?.("change", onChange);
  return {
    get on() {
      return reduced;
    },
    set(on) {
      reduced = on;
    },
    stop() {
      media?.removeEventListener?.("change", onChange);
    },
  };
}

/** The ball meshes of a scene: one mesh for each ball id, and spare meshes for reuse. */
interface BallPool {
  /** Show the balls of a frame. A ball that is gone goes back to the spare list. */
  sync(frames: readonly BallFrame[]): void;
  clear(): void;
}

function createBallPool(model: SceneModel): BallPool {
  const active = new Map<number, Mesh>();
  const spare: Mesh[] = [];
  const take = (id: number): Mesh => {
    let mesh = active.get(id);
    if (!mesh) {
      mesh = spare.pop() ?? model.createBall();
      active.set(id, mesh);
      model.balls.add(mesh);
    }
    return mesh;
  };
  return {
    sync(frames) {
      const seen = new Set<number>();
      for (const ball of frames) {
        seen.add(ball.id);
        applyBallFrame(take(ball.id), ball);
      }
      for (const [id, mesh] of active) {
        if (seen.has(id)) continue;
        model.balls.remove(mesh);
        active.delete(id);
        spare.push(mesh);
      }
    },
    clear() {
      active.clear();
      spare.length = 0;
    },
  };
}

/** Apply one engine event to the scene. */
function applySceneEvent(model: SceneModel, ev: EngineEvent, timeMs: number): void {
  if (ev.type === "light") {
    const insert = model.inserts.get(ev.name);
    if (insert) {
      insert.state = ev.state;
      insert.override = undefined;
    }
    return;
  }
  // "Callout" and "Hud" are text channels. The client shell shows them.
  if (ev.type !== "animate" || TEXT_CHANNELS.has(ev.decoration)) return;
  const node =
    model.decorations.find((n) => n.spec.name === ev.decoration) ??
    model.decorations.find((n) => n.spec.meshId === ev.decoration);
  node?.factory.animate?.(node.object, ev.animation, timeMs);
}

/** Apply the items of a frame, the lamps and the idle pose of each decoration. */
function applySceneFrame(model: SceneModel, frame: RenderFrame, reducedMotion: boolean): void {
  for (const item of frame.items) model.items.get(item.name)?.update(item);
  for (const insert of model.inserts.values()) insert.apply(frame.timeMs, reducedMotion);
  for (const node of model.decorations) node.factory.animate?.(node.object, "idle", frame.timeMs);
}

/** Add the environment map to the scene. Returns the handle, or null when there is none. */
function addEnvironment(deps: RendererDeps, gl: WebGLLike, scene: Scene): EnvironmentHandle | null {
  const environment = deps.environment?.(gl) ?? null;
  if (environment) {
    scene.environment = environment.texture;
    scene.environmentIntensity = 0.45;
  }
  return environment;
}

/** The state of one renderer. The renderer methods hand it to the helpers below. */
interface RendererState {
  readonly gl: WebGLLike;
  readonly model: SceneModel;
  readonly camera: PerspectiveCamera;
  readonly reduced: ReducedMotionFlag;
  readonly balls: BallPool;
  readonly backdropColor: Color;
  readonly art: Backdrop;
  readonly environment: EnvironmentHandle | null;
  transparent: boolean;
  pixelHeight: number;
  lastTimeMs: number;
  disposed: boolean;
}

/** Show the page behind the canvas (on) or the opaque backdrop (off). */
function rendererSetTransparent(state: RendererState, on: boolean): void {
  if (state.disposed || on === state.transparent) return;
  state.transparent = on;
  state.model.scene.background = on ? null : state.backdropColor;
  state.art.setVisible(!on);
  state.gl.setClearColor?.(BRAND.field, on ? 0 : 1);
}

/** Resize the canvas and the camera. A size that is not positive is ignored. */
function rendererResize(
  state: RendererState,
  width: number,
  height: number,
  pixelRatio: number,
): void {
  if (state.disposed || !(width > 0 && height > 0)) return;
  const ratio = capPixelRatio(pixelRatio);
  state.gl.setPixelRatio(ratio);
  state.gl.setSize(width, height, false);
  state.pixelHeight = Math.round(height * ratio);
  state.art.setAspect(width / height);
  fitCamera(state.camera, width / height, state.model.bounds, state.model.viewPoints);
}

/** Draw one frame: the balls, the scene, the backdrop, then the WebGL pass. */
function rendererRender(state: RendererState, frame: RenderFrame): void {
  if (state.disposed) return;
  state.lastTimeMs = frame.timeMs;
  state.balls.sync(frame.balls);
  applySceneFrame(state.model, frame, state.reduced.on);
  state.gl.render(state.model.scene, state.camera);
}

/** Free the decorations, the scene, the environment and the WebGL context. */
function rendererDispose(state: RendererState): void {
  if (state.disposed) return;
  state.disposed = true;
  state.reduced.stop();
  state.balls.clear();
  for (const node of state.model.decorations) node.factory.dispose?.(node.object);
  state.art.dispose();
  state.model.dispose();
  state.environment?.dispose();
  state.gl.dispose();
}

/** Make a renderer with the given platform dependencies. */
export function createRendererWith(
  deps: RendererDeps,
  canvas: HTMLCanvasElement,
  table: TableSpec,
  decorations: ReadonlyMap<string, DecorationFactory<Object3D>>,
): PinballRenderer {
  const gl = deps.createWebGL(canvas);
  gl.shadowMap.enabled = true;
  gl.shadowMap.type = PCFShadowMap;

  const model = buildScene(table, {
    decorations,
    createCanvas: deps.createCanvas,
  });
  const environment = addEnvironment(deps, gl, model.scene);

  const camera = createCamera();
  fitCamera(camera, 1, model.bounds, model.viewPoints);

  const art = createBackdrop();
  model.scene.add(art.mesh);
  gl.setClearColor?.(BRAND.field, 1);

  const state: RendererState = {
    gl,
    model,
    camera,
    reduced: trackReducedMotion(deps.matchMedia?.(REDUCED_MOTION_QUERY) ?? null),
    balls: createBallPool(model),
    backdropColor: new Color(BRAND.field),
    art,
    environment,
    transparent: false,
    pixelHeight: 0,
    lastTimeMs: 0,
    disposed: false,
  };

  const renderer: PinballRenderer = {
    scene: model.scene,
    camera,
    model,
    get reducedMotion() {
      return state.reduced.on;
    },
    get backdropTransparent() {
      return state.transparent;
    },
    get pixelHeight() {
      return state.pixelHeight;
    },
    setReducedMotion(on) {
      state.reduced.set(on);
    },
    setBackdropTransparent(on) {
      rendererSetTransparent(state, on);
    },
    backdrop: art,
    resize(width, height, pixelRatio) {
      rendererResize(state, width, height, pixelRatio);
    },
    render(frame: RenderFrame) {
      rendererRender(state, frame);
    },
    applyEvent(ev: EngineEvent) {
      if (!state.disposed) applySceneEvent(model, ev, state.lastTimeMs);
    },
    dispose() {
      rendererDispose(state);
    },
  };
  return renderer;
}

/** Make a browser renderer. This is the client entry point. */
export function createPinballRenderer(
  canvas: HTMLCanvasElement,
  table: TableSpec,
  decorations: ReadonlyMap<string, DecorationFactory<Object3D>>,
): PinballRenderer {
  return createRendererWith(browserDeps(), canvas, table, decorations);
}

/** The contract entry point (`CreateRenderer`). It is `createPinballRenderer` with a narrower type. */
export const createRenderer: CreateRenderer<HTMLCanvasElement, Object3D> = createPinballRenderer;
